import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Select from "react-select";
import Spinner from "react-bootstrap/Spinner";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosClient from "../../../config/axios.js";
import {
  getOrdersVialBillingColumns,
  customStyles,
} from "./ordersVialBillingTableConfig";

const validationSchema = Yup.object().shape({
  startDate: Yup.date()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .max(
      Yup.ref("endDate"),
      "La fecha de inicio no puede ser mayor que la fecha de fin"
    ),
  endDate: Yup.date()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .min(
      Yup.ref("startDate"),
      "La fecha de fin no puede ser menor que la fecha de inicio"
    ),
});

const estatusOptions = [
  { value: "Pendiente", label: "Pendiente" },
  { value: "Pagado", label: "Pagado" },
  { value: "Olvidado", label: "Olvidado" },
  { value: "Cancelado", label: "Cancelado" },
];

function OrdersVialBillingTable() {
  const [data, setData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(true);

  const fetchData = async (filters) => {
    try {
      const params = {
        start: (currentPage - 1) * rowsPerPage,
        length: rowsPerPage,
        search: filters.searchValue || "",
        estatus: filters.estatus?.value,
        sort: sortField,
        dir: sortDirection,
      };

      if (filters.startDate && filters.endDate) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
      }

      const response = await axiosClient.get(
        `/report-invoice-cartaporte-orders-vial/datatable`,
        { params }
      );

      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      startDate: "",
      endDate: "",
      searchValue: "",
      estatus: "",
    },
    validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      setCurrentPage(1);
      fetchData(values);
    },
  });

  useEffect(() => {
    setLoading(true);
    fetchData(formik.values);
  }, [currentPage, rowsPerPage, sortField, sortDirection]);

  const clearFilters = () => {
    formik.resetForm();
    setLoading(true);
    fetchData({
      startDate: "",
      endDate: "",
      searchValue: "",
      estatus: "",
    });
  };

  return (
    <div className="table-responsive mb-3">
      <DataTable
        columns={getOrdersVialBillingColumns()}
        data={data.data}
        progressPending={loading}
        sortServer
        pagination
        paginationServer
        paginationPerPage={rowsPerPage}
        paginationTotalRows={data.recordsFiltered}
        paginationDefaultPage={currentPage}
        subHeader
        subHeaderAlign="center"
        customStyles={customStyles}
        pointerOnHover
        paginationRowsPerPageOptions={[50, 100, 200, 500]}
        paginationComponentOptions={{
          rowsPerPageText: "Filas por página",
          rangeSeparatorText: "de",
        }}
        noDataComponent="Sin información por mostrar"
        progressComponent={
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        }
        subHeaderComponent={
          <form
            className="row vw-100 align-items-end"
            onSubmit={formik.handleSubmit}
          >
            <div className="col-sm-3 col-md-3 mb-3">
              <label htmlFor="startDate" className="form-label">
                Fecha inicio
              </label>
              <input
                type="date"
                name="startDate"
                className="form-control"
                value={formik.values.startDate}
                onChange={formik.handleChange}
              />
              {formik.errors.startDate && (
                <div className="text-danger small">
                  {formik.errors.startDate}
                </div>
              )}
            </div>

            <div className="col-sm-3 col-md-3 mb-3">
              <label htmlFor="endDate" className="form-label">
                Fecha fin
              </label>
              <input
                type="date"
                name="endDate"
                className="form-control"
                value={formik.values.endDate}
                onChange={formik.handleChange}
              />
              {formik.errors.endDate && (
                <div className="text-danger small">{formik.errors.endDate}</div>
              )}
            </div>

            <div className="col-sm-3 col-md-3 mb-3">
              <Select
                name="estatus"
                options={estatusOptions}
                value={formik.values.estatus}
                placeholder="Filtrar por estatus"
                isClearable
                isSearchable
                onChange={(value) => formik.setFieldValue("estatus", value)}
              />
            </div>

            <div className="col-sm-3 col-md-3 ml-auto mb-3 d-flex gap-2">
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={clearFilters}
              >
                Limpiar filtros
              </button>
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={formik.handleSubmit}
              >
                Buscar
              </button>
            </div>
            <div className="col-auto ms-auto mb-3">
              <div className="input-group">
                <input
                  type="text"
                  name="searchValue"
                  className="form-control form-control-md"
                  placeholder="Buscar..."
                  value={formik.values.searchValue}
                  onChange={formik.handleChange}
                  onKeyDown={(e) => e.key === "Enter" && formik.handleSubmit()}
                />
                <div
                  className="input-group-text btn btn-info"
                  onClick={formik.handleSubmit}
                >
                  <i className="fa fa-search"></i>
                </div>
              </div>
            </div>
          </form>
        }
        onChangePage={(page) => setCurrentPage(page)}
        onChangeRowsPerPage={(newPerPage) => {
          setRowsPerPage(newPerPage);
          setCurrentPage(1);
        }}
        onSort={(column, sortDirection) => {
          setSortDirection(sortDirection);
          setSortField(column.sortField);
          setLoading(true);
          setCurrentPage(1);
        }}
      />
    </div>
  );
}

export default OrdersVialBillingTable;
